/* eslint-disable */
// Session 45 · /notifications inbox — full audit trail of SLA events
// (booking approvals, cancellations, refunds, host requests) with member
// avatars for a consistent Commons look.
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import AppShell from "../components/AppShell";
import MemberAvatar from "../components/MemberAvatar";
import api from "../lib/api";
import { toast } from "sonner";

// --- Event → friendly copy + destination -----------------------------------
// Each notification stores `event`, `role`, `request_id`, `workspace_id`, and
// a free-form `meta` bag. We map common events to human copy + a deep-link.
// Unknown events render generically so the page never blows up.
function eventMeta(n) {
  const e = String(n.event || "").toLowerCase();
  const meta = n.meta || {};
  // Session 52 · Backend writes role="owner" for workspace host notifications;
  // older docs may have "host". Accept both so the host branch renders.
  const rawRole = String(n.role || "").toLowerCase();
  const role = (rawRole === "owner" || rawRole === "host") ? "host" : (rawRole || "member");
  const wsName = meta.workspace_name || meta.name || "your workspace";
  const memberName = meta.member_name || meta.name || meta.member_email || "A member";
  const dt = meta.date || meta.start_at || "";

  // Commons social events (Session 60.2 · Notifications audit) --------------
  // Like + comment notifications. Actor identity comes exclusively from the
  // shared server-side `resolve_display_name` output stored in
  // `meta.actor_name`. Never expose emails, user IDs, or "undefined".
  if (e === "post_liked" || e.includes("post_like")) {
    const actors = Array.isArray(meta.actors) ? meta.actors : [];
    const primary = meta.actor_name || (actors[0] && actors[0].name) || "A member";
    const total = actors.length || 1;
    let title;
    if (total >= 3) {
      title = `${primary} and ${total - 1} others liked your post`;
    } else if (total === 2) {
      const second = (actors[1] && actors[1].name) || "another member";
      title = `${primary} and ${second} liked your post`;
    } else {
      title = `${primary} liked your post`;
    }
    return {
      title,
      body: meta.post_snippet ? `"${meta.post_snippet}"` : "",
      kind: "social",
    };
  }
  if (e === "post_commented" || e.includes("post_comment")) {
    const primary = meta.actor_name || "A member";
    return {
      title: `${primary} commented on your post`,
      body: meta.comment_snippet ? `"${meta.comment_snippet}"` : "",
      kind: "social",
    };
  }
  // Session 60.5 · Direct message notifications (P0 audit fix).
  if (e === "dm_received" || e.includes("dm_")) {
    const primary = meta.actor_name || "A member";
    return {
      title: `${primary} sent you a message`,
      body: meta.message_snippet ? `"${meta.message_snippet}"` : "",
      kind: "social",
    };
  }
  // Session 60.6 · Member-submitted event awaiting admin review.
  if (e === "event_submitted") {
    const primary = meta.actor_name || "A member";
    return {
      title: `${primary} submitted an event for review`,
      body: meta.event_title ? `"${meta.event_title}"${meta.event_city ? ` · ${meta.event_city}` : ""}` : "",
      kind: "request",
    };
  }

  // Reservation lifecycle -------------------------------------------------
  if (e.includes("booking_request") || e === "reserve" || e === "booking_created") {
    if (role === "host") {
      return {
        title: `New booking request for ${wsName}`,
        body: `${memberName} requested ${dt || "a reservation"}. Review and approve within your SLA.`,
        kind: "request",
      };
    }
    return {
      title: `Reservation request sent`,
      body: `Your request at ${wsName} for ${dt || "the selected slot"} is awaiting host approval.`,
      kind: "request",
    };
  }
  if (e.includes("approved") || e.includes("confirmed") || e.includes("accept")) {
    return {
      title: `Booking approved at ${wsName}`,
      body: `${role === "host" ? "You approved" : "Your host approved"} ${dt || "the reservation"}. See you there.`,
      kind: "approval",
    };
  }
  if (e.includes("declined") || e.includes("rejected")) {
    return {
      title: `Booking declined at ${wsName}`,
      body: `${role === "host" ? "You declined" : "Your host declined"} the request${meta.reason ? ` — "${meta.reason}"` : ""}.`,
      kind: "decline",
    };
  }
  if (e.includes("cancel")) {
    return {
      title: `Reservation cancelled at ${wsName}`,
      body: `${role === "host" ? memberName : "You"} cancelled ${dt || "the booking"}${meta.refund_amount ? ` · refund ${meta.refund_amount}` : ""}.`,
      kind: "cancel",
    };
  }
  if (e.includes("refund")) {
    const amt = meta.refund_amount || meta.amount || "";
    return {
      title: `Refund issued${amt ? ` · ${amt}` : ""}`,
      body: `A refund was processed for ${wsName}${meta.reason ? ` — "${meta.reason}"` : ""}.`,
      kind: "refund",
    };
  }
  if (e.includes("payment") && e.includes("fail")) {
    return {
      title: `Payment failed at ${wsName}`,
      body: `${memberName}'s payment did not go through. The reservation is not confirmed.`,
      kind: "warn",
    };
  }
  if (e.includes("payout")) {
    return {
      title: `Payout ${e.includes("failed") ? "failed" : "sent"} · ${wsName}`,
      body: meta.amount ? `${meta.amount} routed via Stripe Connect.` : "See the Reports tab for details.",
      kind: "payout",
    };
  }
  // Fallback -----------------------------------------------------------
  return {
    title: n.title || e.replace(/_/g, " ") || "Notification",
    body: n.body || (typeof meta.message === "string" ? meta.message : ""),
    kind: "generic",
  };
}

function eventLink(n) {
  const meta = n.meta || {};
  const rawRole = String(n.role || meta.role || "").toLowerCase();
  const e = String(n.event || "").toLowerCase();
  // Commons social events → land on /community with the post highlighted.
  // Session 60.7 · Comment notifications also open the comment thread via
  // `?comment=1`, and we prove the post actually renders (not just the
  // feed) via a fetch-if-missing branch in /community itself.
  if (e.includes("post_like") || e.includes("post_comment")) {
    const pid = meta.post_id;
    if (pid) {
      const wantComment = e.includes("post_comment") ? "&comment=1" : "";
      return `/community?post=${encodeURIComponent(pid)}${wantComment}`;
    }
    return "/community";
  }
  // DM notifications → open the actual thread if known.
  if (e === "dm_received" || e.includes("dm_")) {
    const tid = meta.thread_id;
    if (tid) return `/community/messages/${encodeURIComponent(tid)}`;
    return "/community/messages";
  }
  // Member event submissions → admin review page.
  if (e === "event_submitted") {
    return "/admin/events?filter=pending";
  }
  if (rawRole === "host" || rawRole === "owner") {
    if (n.workspace_id) return `/owner/dashboard`;
  }
  if (n.request_id) return `/account/bookings/${n.request_id}`;
  if (meta.workspace_slug) return `/spaces/${meta.workspace_slug}`;
  return null;
}

function accentFor(kind) {
  switch (kind) {
    case "approval": return "#2d7a4f";
    case "refund":   return "#B8913A";
    case "cancel":
    case "decline":  return "#b34141";
    case "warn":     return "#b34141";
    case "payout":   return "#3a6bb8";
    case "request":  return "#B8913A";
    case "social":   return "#B8913A";
    default:         return "rgba(27,35,64,0.55)";
  }
}

function fmtWhen(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    const now = new Date();
    const diffMs = now - d;
    const min = Math.floor(diffMs / 60000);
    if (min < 1) return "just now";
    if (min < 60) return `${min}m ago`;
    const hr = Math.floor(min / 60);
    if (hr < 24) return `${hr}h ago`;
    const day = Math.floor(hr / 24);
    if (day < 7) return `${day}d ago`;
    return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
  } catch { return ""; }
}

export default function Notifications() {
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [total, setTotal] = useState(0);
  const [filter, setFilter] = useState("all"); // all | unread
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const nav = useNavigate();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/notifications?limit=100${filter === "unread" ? "&unread_only=true" : ""}`);
      setItems(r.data?.items || []);
      setUnread(r.data?.unread || 0);
      setTotal(r.data?.total || 0);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load notifications");
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const markAll = async () => {
    if (unread === 0) return;
    setBusy(true);
    try {
      const r = await api.post("/notifications/read-all", {});
      toast.success(`Marked ${r.data?.read_count || 0} notifications as read`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not mark all read");
    } finally { setBusy(false); }
  };

  const onOpen = async (n) => {
    // Mark read then follow link if any
    if (!n.read_at) {
      try {
        await api.post(`/notifications/${n.notification_id}/read`, {});
      } catch { /* ignore */ }
    }
    const to = eventLink(n);
    if (to) nav(to);
    else await load();
  };

  const list = useMemo(() => items, [items]);

  return (
    <AppShell>
      <div style={{ maxWidth: 780, margin: "0 auto", padding: "32px 20px 80px" }}>
        <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", marginBottom: 8, gap: 12, flexWrap: "wrap" }}>
          <div>
            <div className="m-eyebrow" style={{ color: "var(--gold)" }}>Commons</div>
            <h1 style={{ fontFamily: "'Playfair Display', Georgia, serif", fontSize: 34, margin: "4px 0 0" }}>
              Notifications
            </h1>
            <div className="m-muted small" style={{ marginTop: 6 }}>
              {loading ? "Loading…" : `${total} total · ${unread} unread`}
            </div>
          </div>
          <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
            <button
              type="button"
              className={"m-pill light" + (filter === "all" ? " active" : "")}
              onClick={() => setFilter("all")}
              data-testid="notif-filter-all"
            >All</button>
            <button
              type="button"
              className={"m-pill light" + (filter === "unread" ? " active" : "")}
              onClick={() => setFilter("unread")}
              data-testid="notif-filter-unread"
            >Unread {unread > 0 && <em style={{ marginLeft: 4, opacity: 0.7, fontStyle: "normal" }}>{unread}</em>}</button>
            <button
              type="button"
              className="m-btn-ghost"
              style={{ padding: "6px 12px", fontSize: 12 }}
              onClick={markAll}
              disabled={busy || unread === 0}
              data-testid="notif-mark-all-read"
            >Mark all read</button>
          </div>
        </div>

        <div style={{ marginTop: 20 }}>
          {loading ? (
            <div className="m-empty small" data-testid="notif-loading">Loading notifications…</div>
          ) : list.length === 0 ? (
            <div className="m-empty" data-testid="notif-empty" style={{ padding: "48px 20px", textAlign: "center" }}>
              <div style={{ fontSize: 36, marginBottom: 10, opacity: 0.5 }} aria-hidden="true">✦</div>
              <div style={{ fontSize: 15, fontWeight: 500, marginBottom: 6 }}>
                {filter === "unread" ? "No unread notifications" : "You're all caught up"}
              </div>
              <div className="m-muted small">
                Approvals, cancellations, refunds, and messages will appear here.
              </div>
            </div>
          ) : (
            <ul style={{ listStyle: "none", padding: 0, margin: 0 }} data-testid="notif-list">
              {list.map((n) => {
                const em = eventMeta(n);
                const isUnread = !n.read_at;
                const accent = accentFor(em.kind);
                const to = eventLink(n);
                const meta = n.meta || {};
                const avatarUser = {
                  // Session 58.5 · Prefer the ACTOR's id (the member who
                  // triggered the notification) over the recipient's id so
                  // the avatar + testid + click-through all match the right
                  // person. Fall back to legacy fields, and only use
                  // `n.user_id` (the recipient) as a last resort so the
                  // avatar always has *some* identity to render.
                  // Session 60.2 · Notifications audit — also read
                  // `actor_name` / `actor_picture` written by like/comment
                  // notifiers so the resolved display name lands here.
                  user_id: meta.actor_user_id || meta.user_id || meta.member_id || n.user_id,
                  name: meta.actor_name || meta.member_name || meta.name || "Métier Member",
                  picture: meta.actor_picture || meta.member_picture || meta.picture,
                  initials: meta.actor_initials || meta.member_initials || meta.initials,
                };
                return (
                  <li
                    key={n.notification_id}
                    style={{
                      display: "flex", gap: 12,
                      padding: "14px 16px",
                      borderRadius: 10,
                      background: isUnread ? "rgba(184,145,58,0.06)" : "#fff",
                      border: "1px solid " + (isUnread ? "rgba(184,145,58,0.35)" : "rgba(27,35,64,0.08)"),
                      marginBottom: 8,
                      cursor: to ? "pointer" : "default",
                      transition: "background 120ms, border 120ms",
                    }}
                    onClick={() => onOpen(n)}
                    data-testid={`notif-item-${n.notification_id}`}
                  >
                    <div style={{
                      width: 4, borderRadius: 2, background: accent,
                      flexShrink: 0, alignSelf: "stretch",
                    }} />
                    <MemberAvatar user={avatarUser} size={36} clickable={Boolean(avatarUser.user_id)} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{
                        display: "flex", alignItems: "baseline",
                        justifyContent: "space-between", gap: 12, flexWrap: "wrap",
                      }}>
                        <div style={{
                          fontWeight: isUnread ? 600 : 500,
                          fontSize: 14,
                          color: "var(--m-navy, #1B2340)",
                          lineHeight: 1.35,
                        }}>
                          {em.title}
                        </div>
                        <div style={{
                          fontSize: 11,
                          color: "rgba(27,35,64,0.55)",
                          whiteSpace: "nowrap",
                        }}>
                          {fmtWhen(n.created_at)}
                        </div>
                      </div>
                      {em.body && (
                        <div style={{
                          fontSize: 13,
                          color: "rgba(27,35,64,0.70)",
                          marginTop: 4,
                          lineHeight: 1.5,
                        }}>
                          {em.body}
                        </div>
                      )}
                      <div style={{
                        display: "flex", gap: 10, marginTop: 8,
                        alignItems: "center", flexWrap: "wrap",
                      }}>
                        <span style={{
                          fontSize: 10, letterSpacing: "0.12em",
                          textTransform: "uppercase", fontWeight: 600,
                          color: accent,
                        }}>
                          {em.kind}
                        </span>
                        {to && (
                          <Link
                            to={to}
                            onClick={(e) => e.stopPropagation()}
                            style={{
                              fontSize: 12,
                              color: "var(--m-navy, #1B2340)",
                              textDecoration: "underline",
                              textUnderlineOffset: 3,
                            }}
                            data-testid={`notif-item-link-${n.notification_id}`}
                          >
                            View →
                          </Link>
                        )}
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    </AppShell>
  );
}
